import * as React from 'react';
import {useComponentVisible} from "../../hooks/useComponentVisible";
import styles from './styles/MultiSelect.module.scss';
import FontAwesome from "./FontAwesome";
import classNames from 'classnames';
import {useCallback} from "react";

interface IOption {
    value: any,
    title: string,
}

const SelectOption: React.FC<{
    selected: boolean,
    isCategory?: boolean,
    title: string,
    handleSelect: () => void,
}> = props => {
    const optionClassName = classNames({
        [styles.option]: true,
        [styles.optionSelected]: props.selected,
        [styles.optionCategory]: props.isCategory,
    })

    return <li onClick={props.handleSelect} role={"button"} className={optionClassName}>
        <input checked={props.selected} type={"checkbox"}/>
        <span>{props.title}</span>
    </li>
}

const MultiSelect: React.FC<{
    options: IOption[] | {
        [category: string]: IOption[]
    },
    selected: any[],
    setSelected: React.SetStateAction<any>,
    className?: string,
    [restProp: string]: any,
}> = props => {
    const {options, selected, setSelected, className = "", ...restProps} = props;
    const numOptions: number = Array.isArray(options) ? options.length : Object.values(options).reduce((acc, curr) => {
        return acc + curr.length;
    }, 0);
    const {ref, isComponentVisible, setIsComponentVisible} = useComponentVisible();

    const DropdownButton = useCallback(() => {
        let buttonText;
        if (props.selected.length === 0) {
            buttonText = "None selected";
        } else if (props.selected.length === numOptions) {
            buttonText = `All selected ${numOptions}`;
        } else {
            buttonText = `${props.selected.length} selected`;
        }

        return <button onClick={() => setIsComponentVisible(v => !v)}>
            {buttonText} <FontAwesome name={"fa-chevron-down"} prefix={'fas'}/>
        </button>
    }, [props.selected.length]);

    function select(value: any, force: boolean = false) {
        // if force is enabled, it won't deselect if already selected
        if (Array.isArray(value)) { // select multiple at once, don't want to render each time
            const currentValue = [...props.selected];
            value.forEach(v => {
                const isSelected = !!currentValue.find(s => s === v);
                if (isSelected && !force) {
                    currentValue.splice(currentValue.findIndex(s => s === v), 1);
                } else if (!isSelected) {
                    currentValue.push(v);
                }
            });
            props.setSelected(currentValue);
        } else {
            const isSelected = !!props.selected.find(s => s === value);
            if (isSelected && !force) {
                props.setSelected(props.selected.filter(v => v !== value));
            } else if (!isSelected) {
                props.setSelected([...props.selected, value]);
            }
        }
    }

    let content: JSX.Element[] = [];
    if (Array.isArray(options)) {
        options.forEach(o => {
            const isSelected = !!props.selected.find(s => s === o.value);
            content.push(<SelectOption
                handleSelect={() => select(o.value)}
                selected={isSelected}
                title={o.title}
            />);
        })
    } else {
        Object.entries(options).forEach(([category, options]) => {
            const allOptionsSelected = options.every(o => props.selected.find(s => s === o.value));
            content.push(<SelectOption
                handleSelect={() => {
                    select(options.map(o => o.value), !allOptionsSelected);
                }}
                selected={allOptionsSelected}
                title={category}
                isCategory={true}
                key={category}
            />);
            options.forEach(o => {
                content.push(<SelectOption
                    handleSelect={() => select(o.value)}
                    selected={!!props.selected.find(s => s === o.value)}
                    title={o.title}
                    key={o.value}
                />)
            })
        })
    }

    const selectAllSelected = props.selected.length === numOptions;

    const containerClassName = classNames({
        [styles.container]: true,
        [className]: className,
    })

    return <div ref={ref} className={containerClassName} {...restProps}>
        <DropdownButton/>
        {isComponentVisible && <div>
            <ul>
                <SelectOption
                    selected={selectAllSelected}
                    title={"Select all"}
                    key={"select-all"}
                    handleSelect={() => {
                        if (props.selected.length === numOptions) { // if all already selected, deselect all
                            props.setSelected([]);
                        } else if (Array.isArray(options)) {
                            select(options.map(o => o.value), true);
                        } else {
                            select(Object.values(options).reduce((acc, curr) => {
                                curr.forEach(c => acc.push(c.value));
                                return acc;
                            }, []), true);
                        }
                    }}
                />
                <span>{content}</span>
            </ul>
        </div>}
    </div>
}

export default MultiSelect;
